import { Button, Dialog, DialogContent, DialogTitle, Grid, makeStyles } from '@material-ui/core';
import { defer } from '@thalesrc/js-utils';
import React, { useCallback, useContext } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router';

import { HealthInsurancesModel, saveHealthInsurances, updateHealthInsurances } from 'api/hs/health-insurances';
import TextField from 'Common/Form/TextField';
import { AlertContext } from 'context/alert.context';
import { useAsyncEffect } from 'utils';

import DateInput from '../../../../../../Common/Form/DateInput';

const useStyles = makeStyles(theme => ({
  dialog: {
    width: '100%',
  },
  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  sideGrid: {
    display: 'grid',
    gridTemplateColumns: '100%',
    gridTemplateRows: '1fr auto',
  },
  dialogPaper: {
    width: '50vw',
  },
  dialogScroll: {
    alignItems: 'flex-start',
  },
}));

interface Props {
  open: boolean;

  onClose(success: boolean): void;

  data: HealthInsurancesModel;
}

export default function HealthInsurancesForm({ open, onClose, data }: Props) {
  const classes = useStyles();
  const { setValue, getValues, reset, trigger, ...methods } = useForm();
  const { showMessage } = useContext(AlertContext);
  const { employeeId } = useParams<{ employeeId: string }>();

  const handleSubmit = useCallback(
    async values => {
      try {
        if (!data?.id) {
          await saveHealthInsurances({ ...values, employeeId });
          showMessage('Ekleme işlemi başarılı.', 'success');
        } else {
          await updateHealthInsurances({
            employeeId,
            id: data.id,
            ...values,
          });
          showMessage('Güncelleme işlemi başarılı.', 'success');
        }
        onClose(true);
      } catch (err) {
        showMessage('İşlem sırasında sorun oluştu.', 'error');
        onClose(false);
      }
    },
    [data, onClose, showMessage, employeeId]
  );

  useAsyncEffect(async () => {
    if (!open) {
      return;
    }
    await defer();

    if (data) {
      setValue('description', data.description);
      setValue('agencyName', data.agencyName);
      setValue('endDate', data.endDate);
      setValue('startDate', data.startDate);
      setValue('policyNumber', data.policyNumber);
      trigger();
    } else {
      reset({
        description: '' as any,
        agencyName: '' as any,
        endDate: '' as any,
        policyNumber: '' as any,
        startDate: '' as any,
      });
    }
  }, [data, setValue, reset, trigger, open]);

  return (
    <Dialog
      PaperProps={{ style: { maxWidth: 450 } }}
      open={open}
      keepMounted
      onClose={onClose.bind(null, false)}
      classes={{ paper: classes.dialogPaper, scrollPaper: classes.dialogScroll }}>
      <DialogTitle className={classes.dialogTitle}>
        {data?.id ? 'Özel Sağlık Sigortası Güncelle' : 'Özel Sağlık Sigortası Ekle'}
      </DialogTitle>
      <DialogContent>
        <FormProvider {...{ ...methods, reset, getValues, setValue, trigger }}>
          <form noValidate className="mt-2" onSubmit={methods.handleSubmit(handleSubmit)}>
            <Grid container spacing={1}>
              <Grid item md={6} sm={6} lg={6}>
                <TextField required name="agencyName" label="Sigorta Acentesi" />
              </Grid>
              <Grid item md={6} sm={6} lg={6}>
                <TextField required name="policyNumber" type="number" label="Police No" />
              </Grid>
              <Grid item md={6} sm={6} lg={6}>
                <DateInput type="date" required name="startDate" label="Başlangıç Tarihi" />
              </Grid>
              <Grid item md={6} sm={6} lg={6}>
                <DateInput type="date" required name="endDate" label="Bitiş Tarihi" />
              </Grid>
              <Grid item md={12} sm={12} lg={12}>
                <TextField name="description" label="Açıklama" />
              </Grid>
              <Grid item md={12} className="text-right">
                <Button variant="contained" type="submit" color="primary" className="mr-2">
                  {data?.id ? 'Güncelle' : 'Kaydet'}
                </Button>
                <Button variant="contained" type="button" onClick={onClose.bind(null, false)}>
                  Vazgeç
                </Button>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
